import React, { useEffect, useMemo, useRef, useState } from "react";
import type { SelectOption } from "../../features/onboarding/types";

type SearchableSelectProps = {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
};

export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = "Select an option",
  searchPlaceholder = "Search...",
  disabled = false,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleOutside = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const selected = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => option.label.toLowerCase().includes(term));
  }, [options, query]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className="flex min-h-[44px] w-full items-center justify-between rounded-xl border border-[#D6DADF] bg-white px-3 py-2 text-left text-base text-[#111827] shadow-sm"
      >
        <span className={selected ? "" : "text-[#9CA3AF]"}>
          {selected?.label ?? placeholder}
        </span>
        <span className="material-icons text-[20px] text-[#6B7280]">expand_more</span>
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white shadow-xl">
          <div className="border-b border-[#F1F5F9] p-3">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={searchPlaceholder}
              className="h-11 w-full rounded-xl border border-[#D6DADF] px-3 text-base outline-none focus:border-[#93C893]"
            />
          </div>
          <div className="max-h-64 overflow-y-auto p-2">
            {filteredOptions.length === 0 && (
              <div className="px-3 py-4 text-sm text-[#6B7280]">No matches found.</div>
            )}
            {filteredOptions.map((option) => {
              const active = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                    setQuery("");
                  }}
                  className={`flex min-h-[44px] w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm ${
                    active ? "bg-[#EEF9EE] text-[#0F5132]" : "hover:bg-[#F8FAFC]"
                  }`}
                >
                  <span>{option.label}</span>
                  {active && <span className="material-icons text-[18px]">check</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
